import { useMemo, useState } from 'react';
import { generatePath, Outlet } from 'react-router-dom';
import {
    IconChecks,
    IconCreditCard,
    IconEdit,
    IconEye,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import { subscriptionsTexts, commonTexts } from 'consts/text';
import { AppConfirmModal, AppTable, AppTableProps } from 'components/organisms';
import { Option, TopAppMenu } from 'components/atoms';
import { FullInstitutionSubscriptionDTO, SubscriptionType } from 'types';
import { useSearchFilter } from 'hooks/useSearchFilter/useSearchFilter';
import { appPaths } from 'consts/paths/paths';
import { fullInstitutionSubscriptionDTOFields } from 'consts/subscription/fullInstitutionSubscriptionDTOFields';
import {
    AppInstitutionSubscriptionContext,
    AppInstitutionSubscriptionContextProps,
} from 'contexts/AppInstitutionSubscriptionContext';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useAdminInstitutionsPageNetworkManage } from './hooks/useAdminInstitutionsPageNetworkManage';
import { columns, registrationDateColumnKey } from './AdminInstitutionsPage.consts';

const adminInstitutionsTabs = {
    DEFAULT: 'DEFAULT',
    BLOCKED: 'BLOCKED',
} as const;

type AdminInstitutionsTab = (typeof adminInstitutionsTabs)[keyof typeof adminInstitutionsTabs];

export const AdminInstitutionsPage = () => {
    const [selectedInstitutionSubscriptionDataItem, setSelectedInstitutionSubscriptionDataItem] =
        useState<FullInstitutionSubscriptionDTO | null>(null);
    const [institutionPendingDelete, setInstitutionPendingDelete] =
        useState<FullInstitutionSubscriptionDTO | null>(null);
    const [activeTab, setActiveTab] = useState<AdminInstitutionsTab>(adminInstitutionsTabs.DEFAULT);
    const { searchFilterDebounceValue, handleSearchInput } = useSearchFilter({});
    const { statefulNavigate } = useNavigateWithState();

    const statuses = useMemo<SubscriptionType[] | undefined>(() => {
        if (activeTab === adminInstitutionsTabs.BLOCKED) {
            return [SubscriptionType.BLOCKED];
        }

        return [SubscriptionType.ACTIVE, SubscriptionType.TESTING, SubscriptionType.TO_VERIFY];
    }, [activeTab]);

    const {
        institutionSubscriptionDataItems,
        isInstitutionSubscriptionDataItemsInitialLoading,
        isInstitutionSubscriptionDataItemsRefetching,
        isBlockSubscriptionLoading,
        isVerifyInstitutionLoading,
        isDeleteInstitutionLoading,
        isInstitutionSubscriptionDataItemsError,
        blockSubscription,
        verifyInstitution,
        deleteInstitution,
    } = useAdminInstitutionsPageNetworkManage({
        search: searchFilterDebounceValue,
        statuses,
    });

    const handleDetailsClick = (fullInstitutionSubscriptionDTO: FullInstitutionSubscriptionDTO) =>
        statefulNavigate(
            generatePath(appPaths.app.admin.institutions.details.pickedInstitution.base, {
                subscriptionId:
                    fullInstitutionSubscriptionDTO[
                        fullInstitutionSubscriptionDTOFields.subscriptionBasicData
                    ].id,
            }),
        );

    const handleAddSubscriptionClick = (
        fullInstitutionSubscriptionDTO: FullInstitutionSubscriptionDTO,
    ) => {
        statefulNavigate(appPaths.app.admin.institutions['add-subscription']);
        setSelectedInstitutionSubscriptionDataItem(fullInstitutionSubscriptionDTO);
    };

    const handleEditSubscriptionClick = (
        fullInstitutionSubscriptionDTO: FullInstitutionSubscriptionDTO,
    ) => {
        statefulNavigate(appPaths.app.admin.institutions['edit-subscription']);
        setSelectedInstitutionSubscriptionDataItem(fullInstitutionSubscriptionDTO);
    };

    const handleBlockSubscriptionClick = (
        fullInstitutionSubscriptionDTO: FullInstitutionSubscriptionDTO,
    ) => {
        blockSubscription(
            fullInstitutionSubscriptionDTO[fullInstitutionSubscriptionDTOFields.institution].id,
        );
    };

    const handleDeleteSubscriptionClick = (
        fullInstitutionSubscriptionDTO: FullInstitutionSubscriptionDTO,
    ) => {
        setInstitutionPendingDelete(fullInstitutionSubscriptionDTO);
    };

    const handleInstitutionDeleteConfirmation = () => {
        const institutionId =
            institutionPendingDelete?.[fullInstitutionSubscriptionDTOFields.institution].id;

        if (!institutionId) {
            return;
        }

        deleteInstitution(institutionId, {
            onSuccess: () => setInstitutionPendingDelete(null),
            onError: () => setInstitutionPendingDelete(null),
        });
    };

    const handleVerifyClick = (fullInstitutionSubscriptionDTO: FullInstitutionSubscriptionDTO) => {
        verifyInstitution(
            fullInstitutionSubscriptionDTO[fullInstitutionSubscriptionDTOFields.institution].id,
        );
    };

    const institutionSubscriptionDataContext: AppInstitutionSubscriptionContextProps =
        useMemo(() => {
            if (!selectedInstitutionSubscriptionDataItem) {
                return { institutionSubscriptionDataItem: null };
            }
            return { institutionSubscriptionDataItem: selectedInstitutionSubscriptionDataItem };
        }, [selectedInstitutionSubscriptionDataItem]);

    const columnsToRender = useMemo(() => {
        if (activeTab === adminInstitutionsTabs.BLOCKED) {
            return columns.filter((column) => column.key !== registrationDateColumnKey);
        }

        return columns;
    }, [activeTab]);

    const options: Option<FullInstitutionSubscriptionDTO>[] = [
        {
            label: commonTexts.actionLabels.details,
            onClick: handleDetailsClick,
            Icon: <IconEye />,
        },
        {
            label: subscriptionsTexts.list.options.addSubscription,
            onClick: handleAddSubscriptionClick,
            checkIfVisible: ({ subscriptionBasicData }) =>
                !['ACTIVE', 'TO_VERIFY'].includes(subscriptionBasicData.type),
            Icon: <IconPlus />,
        },
        {
            label: subscriptionsTexts.list.options.editSubscription,
            onClick: handleEditSubscriptionClick,
            checkIfVisible: ({ subscriptionBasicData }) => subscriptionBasicData.type === 'ACTIVE',
            Icon: <IconEdit />,
        },
        {
            label: subscriptionsTexts.list.options.block,
            checkIfVisible: ({ subscriptionBasicData }) => subscriptionBasicData.type === 'ACTIVE',
            onClick: handleBlockSubscriptionClick,
            Icon: <IconCreditCard />,
        },
        {
            label: subscriptionsTexts.list.options.delete,
            onClick: handleDeleteSubscriptionClick,
            Icon: <IconTrash />,
        },
        {
            label: subscriptionsTexts.list.options.verify,
            checkIfVisible: ({ subscriptionBasicData }) =>
                subscriptionBasicData.type === 'TO_VERIFY',
            onClick: handleVerifyClick,
            Icon: <IconChecks />,
        },
    ];

    const filters: AppTableProps<FullInstitutionSubscriptionDTO>['tableManageUtilsProps'] = {
        searchInputProps: {
            defaultValue: searchFilterDebounceValue,
            onInput: handleSearchInput,
        },
    };

    return (
        <AppInstitutionSubscriptionContext.Provider value={institutionSubscriptionDataContext}>
            <Outlet />
            <AppConfirmModal
                open={!!institutionPendingDelete}
                title={subscriptionsTexts.list.deleteInstitutionConfirmation.title}
                text={subscriptionsTexts.list.deleteInstitutionConfirmation.text.replace(
                    '{institutionName}',
                    institutionPendingDelete?.[fullInstitutionSubscriptionDTOFields.institution]
                        .name || '',
                )}
                okButtonProps={{
                    title: commonTexts.actionLabels.delete,
                    danger: true,
                }}
                cancelButtonProps={{ title: commonTexts.actionLabels.reject }}
                onOk={handleInstitutionDeleteConfirmation}
                onCancel={() => setInstitutionPendingDelete(null)}
                confirmActionLoading={isDeleteInstitutionLoading}
            />
            <TopAppMenu
                selectedKeys={[activeTab]}
                onClick={({ key }) => setActiveTab(key as AdminInstitutionsTab)}
                items={[
                    {
                        key: adminInstitutionsTabs.DEFAULT,
                        label: subscriptionsTexts.list.tabs.other,
                    },
                    {
                        key: adminInstitutionsTabs.BLOCKED,
                        label: subscriptionsTexts.general.subscriptionTypes.blocked,
                    },
                ]}
            />
            <AppTable
                title={subscriptionsTexts.list.title}
                emptyInfo={
                    activeTab === adminInstitutionsTabs.BLOCKED
                        ? subscriptionsTexts.list.emptyBlockedInfo
                        : subscriptionsTexts.list.emptyInfo
                }
                options={options}
                columns={columnsToRender}
                rowKey={({ subscriptionBasicData }) => subscriptionBasicData.id}
                dataSource={institutionSubscriptionDataItems}
                isDataInitialLoading={isInstitutionSubscriptionDataItemsInitialLoading}
                isDataRefetching={isInstitutionSubscriptionDataItemsRefetching}
                isOptionsLoading={
                    isBlockSubscriptionLoading ||
                    isVerifyInstitutionLoading ||
                    isDeleteInstitutionLoading
                }
                isError={isInstitutionSubscriptionDataItemsError}
                tableManageUtilsProps={filters}
            />
        </AppInstitutionSubscriptionContext.Provider>
    );
};
